// useRemotePlayback — 把 <video> 投到外部螢幕（APP#18 web B）。
//
// 用 W3C **Remote Playback API**（video.remote）：
//   - watchAvailability(cb)：有可投裝置才顯示投屏鈕（cb(true/false)）。
//   - prompt()：觸發瀏覽器原生的「選擇投屏裝置」UI（Chrome → Cast 裝置）。
//   - connect/connecting/disconnect 事件 → 反映連線狀態。
// 不支援（!('remote' in HTMLMediaElement.prototype)）則 supported=false，UI 隱藏鈕。
//
// 註：完整 Google Cast SDK（自訂 receiver / 佇列 / 媒體中繼）屬重量級，本 MVP
// 以標準 Remote Playback API 提供「投到大螢幕」基本能力；進階 Cast 列為缺口
// （見 issue #18 / spec 15 as-built）。
//
// 純特性偵測抽到 pipSupport 同層的 remoteSupported()，供單元測試。

import { useCallback, useEffect, useState } from 'react';

/** RemotePlayback 介面（W3C；型別擴充，避免依賴 lib.dom 版本差異）。 */
interface RemotePlayback extends EventTarget {
  state: 'connecting' | 'connected' | 'disconnected';
  watchAvailability(callback: (available: boolean) => void): Promise<number>;
  cancelWatchAvailability(id?: number): Promise<void>;
  prompt(): Promise<void>;
}
// remote / disableRemotePlayback 由 lib.dom 提供；用交集擴充 remote 為自訂介面型別
// （lib.dom 的 RemotePlayback 與本檔的擴充介面欄位不完全一致，故覆寫）。
type RemoteVideoElement = Omit<HTMLVideoElement, 'remote'> & { remote?: RemotePlayback };

export interface RemotePlaybackController {
  /** 此瀏覽器是否支援 Remote Playback API。 */
  supported: boolean;
  /** 是否偵測到至少一個可投裝置（watchAvailability 回 true）。 */
  available: boolean;
  /** 目前是否已連到外部螢幕。 */
  connected: boolean;
  /** 觸發裝置選擇 UI。回傳是否成功觸發。 */
  prompt(): Promise<boolean>;
}

/** 此瀏覽器是否支援 Remote Playback API。 */
export function remoteSupported(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'remote' in HTMLMediaElement.prototype;
}

export function useRemotePlayback(videoRef: React.RefObject<HTMLVideoElement | null>, ready: boolean): RemotePlaybackController {
  const supported = remoteSupported();
  const [available, setAvailable] = useState(false);
  const [connected, setConnected] = useState(false);

  // watchAvailability + 連線狀態事件。
  useEffect(() => {
    if (!supported) return;
    const v = videoRef.current as RemoteVideoElement | null;
    const remote = v?.remote;
    if (!remote || v?.disableRemotePlayback) return;

    let watchId: number | undefined;
    let cancelled = false;
    const onAvail = (a: boolean) => {
      if (!cancelled) setAvailable(a);
    };
    remote.watchAvailability(onAvail).then(
      (id) => {
        if (cancelled) {
          void remote.cancelWatchAvailability(id).catch(() => {});
        } else {
          watchId = id;
        }
      },
      () => {
        /* 部分平台只在 prompt() 時才知可用性 → 仍顯示鈕（available 維持 false 時由 supported 兜底） */
      },
    );

    const sync = () => setConnected(remote.state === 'connected');
    remote.addEventListener('connect', sync);
    remote.addEventListener('connecting', sync);
    remote.addEventListener('disconnect', sync);
    sync();

    return () => {
      cancelled = true;
      void remote.cancelWatchAvailability(watchId).catch(() => {});
      remote.removeEventListener('connect', sync);
      remote.removeEventListener('connecting', sync);
      remote.removeEventListener('disconnect', sync);
    };
  }, [supported, videoRef, ready]);

  const prompt = useCallback(async (): Promise<boolean> => {
    const v = videoRef.current as RemoteVideoElement | null;
    const remote = v?.remote;
    if (!remote) return false;
    try {
      await remote.prompt();
      return true;
    } catch {
      // 使用者取消選擇 / 無裝置 → 不崩潰
      return false;
    }
  }, [videoRef]);

  return { supported, available, connected, prompt };
}
